import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Inject,
  OnInit,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { Router } from '@angular/router';
import { EventTemplateCardData } from '@models/design-templates/event-template-card-data.model';
import { EventTemplateFont } from '@models/design-templates/event-template-font.model';
import { EventTemplateVisual } from '@models/design-templates/event-template-visual.model';
import { EventAttendee } from '@models/event-attendance/event-attendee.model';
import { RSVPOptionType } from '@models/event-attendance/rsvp-option-type.enum';
import { EventConfirmationPageCardType } from '@models/events/enums/event-confirmation-page-card-type.enum';
import { EventAttendeeTicketOrder } from '@models/events/event-attendee-ticket-order.model';
import { EventAttendeeTicket } from '@models/events/event-attendee-ticket.model';
import { EventConfirmationPageCard } from '@models/events/event-confirmation-page-card.enum';
import { EventRSVPConfirmationPageSectionType } from '@models/events/event-rsvp-confirmation-page-section-type.enum';
import { EventType } from '@models/events/event-type.enum';
import { EventWidget } from '@models/events/event-widget.enum';
import { Event } from '@models/events/event.model';
import { OneTimePricingPlan } from '@models/pricing-plans/one-time-pricing-plan.model';
import { User } from '@models/users/user.model';
import { EventInfoComponent } from '@modules/events/components/event-info/event-info.component';
import { GenericAfterAttendEventCardComponent } from '@modules/events/dialogs/generic-after-attend-event/components/generic-after-attend-event-card/generic-after-attend-event-card.component';
import { GenericAfterAttendSectionCardComponent } from '@modules/events/dialogs/generic-after-attend-event/components/generic-after-attend-section-card/generic-after-attend-section-card.component';
import { GetBoughtTicketsDialog } from '@modules/events/dialogs/get-bought-tickets/get-bought-tickets.dialog';
import { RsvpQrCodeDialog } from '@modules/events/dialogs/rsvp-qr-code/rsvp-qr-code.dialog';
import { AddToCalendarDialog } from '@modules/shared/dialogs/add-to-calendar/add-to-calendar.dialog';
import { LogoCustomFillComponent } from '@modules/shared/logo-custom-fill/logo-custom-fill.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '@services/auth/auth.service';
import { UserService } from '@services/auth/user.service';
import { EventTemplateService } from '@services/events/event-template.service';
import { EventService } from '@services/events/event.service';
import { PricingPlanService } from '@services/pricing-plans/pricing-plan.service';
import { NotificationService } from '@services/shared/notification.service';
import { ScreenWidthService } from '@services/shared/screen-width.service';
import { AppRoutes } from '../../../../routes';

@Component({
  selector: 'app-generic-after-attend-event',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    GenericAfterAttendSectionCardComponent,
    EventInfoComponent,
    LogoCustomFillComponent,
    GenericAfterAttendEventCardComponent,
  ],
  templateUrl: './generic-after-attend-event.dialog.html',
  styleUrl: './generic-after-attend-event.dialog.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    EventTemplateService,
    EventService,
    UserService,
    NotificationService,
    ScreenWidthService,
    PricingPlanService,
  ],
})
export class GenericAfterAttendEventDialog implements OnInit {
  event?: Event;
  rsvpType: RSVPOptionType = RSVPOptionType.YES;
  onWaitlist: boolean = false;
  onPendingList: boolean = false;
  eventConfirmationPage?: EventConfirmationPageCard;
  loggedUser?: User;
  actionInProgress?: 'addToWallet';
  eventAttendee?: EventAttendee;
  isDesktop: boolean = true;
  cards = [
    EventConfirmationPageCardType.CONFIRMATION,
    EventConfirmationPageCardType.SOCIAL_MEDIA,
    EventConfirmationPageCardType.CREATE_EVENT,
  ];
  withTickets?: boolean = false;
  ticketOrder?: EventAttendeeTicketOrder;
  myOrders?: EventAttendeeTicketOrder[];
  designTemplates: EventTemplateCardData[] = [];
  oneTimePricingPlans?: OneTimePricingPlan[];

  constructor(
    public dialogRef: MatDialogRef<GenericAfterAttendEventDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
    private eventService: EventService,
    private translate: TranslateService,
    private notificationService: NotificationService,
    private viewContainerRef: ViewContainerRef,
    private screenWidthService: ScreenWidthService,
    private authService: AuthService,
    private eventTemplateService: EventTemplateService,
    private pricingPlanService: PricingPlanService,
    private router: Router,
  ) {
    if (data?.event) {
      this.rsvpType = data.rsvpType;
      this.event = data.event;
      this.eventAttendee = data.eventAttendee;
      this.onWaitlist = data.onWaitlist;
      this.onPendingList = data.onPendingList;
      this.withTickets = data.withTickets;
      this.ticketOrder = data.ticketOrder;

      this.screenWidthService
        .isDesktop()
        .pipe(takeUntilDestroyed())
        .subscribe((isDesktop) => (this.isDesktop = isDesktop));

      this.authService.userSubject
        .pipe(takeUntilDestroyed())
        .subscribe((user) => (this.loggedUser = user));

      this.eventTemplateService.fetch().subscribe({
        next: (templates) => {
          this.designTemplates = templates;
        },
      });

      this.pricingPlanService.getOneTimePricingPlans().subscribe({
        next: (plans) => {
          this.oneTimePricingPlans = plans;
        },
        error: (err) => {},
      });
    }
  }

  getHeaderStatus() {
    switch (this.afterScreenRsvpType) {
      case RSVPOptionType.YES:
        return this.translate.instant('APP.EVENT_AFTER_ATTEND.GENERIC.SUCCESS');
      case RSVPOptionType.NO:
        return this.translate.instant(
          'APP.EVENT_AFTER_ATTEND.GENERIC.NO_RESPONSE',
        );
      case RSVPOptionType.MAYBE:
        return this.translate.instant(
          'APP.EVENT_AFTER_ATTEND.GENERIC.MAYBE_RESPONSE',
        );
      case RSVPOptionType.WAITLIST:
        return this.translate.instant(
          'APP.EVENT_AFTER_ATTEND.GENERIC.WAIT_LIST_RESPONSE',
        );
      case RSVPOptionType.PENDING_APPROVAL:
        return this.translate.instant(
          'APP.EVENT_AFTER_ATTEND.GENERIC.PENDING_RESPONSE',
        );
      case RSVPOptionType.WITHDRAW:
        return this.translate.instant(
          'APP.EVENT_AFTER_ATTEND.GENERIC.WITHDRAW_RESPONSE',
        );
      default:
        return '';
    }
  }
  ngOnInit(): void {
    if (!this.event) return;
    this.getBoughtTickets();

    if (this.withTickets && this.event.hasApp(EventWidget.BOOKING_SLOTS)) {
      this.cards = [
        EventConfirmationPageCardType.CONFIRMATION,
        EventConfirmationPageCardType.SOCIAL_MEDIA,
      ];
    }

    if (!this.loggedUser || this.loggedUser.id === 0) {
      this.cards = [
        EventConfirmationPageCardType.CONFIRMATION,
        EventConfirmationPageCardType.CREATE_ACCOUNT,
        EventConfirmationPageCardType.SOCIAL_MEDIA,
        EventConfirmationPageCardType.CREATE_EVENT,
      ];
    }

    // let pageType: EventConfirmationPageCardType;

    // switch (this.afterScreenRsvpType) {
    //   case RSVPOptionType.NO:
    //     pageType = EventConfirmationPageCardType.NO;
    //     break;
    //   case RSVPOptionType.WAITLIST:
    //     pageType = EventConfirmationPageCardType.WAITLIST;
    //     break;
    //   case RSVPOptionType.MAYBE:
    //     pageType = EventConfirmationPageCardType.MAYBE;
    //     break;
    //   case RSVPOptionType.PENDING_APPROVAL:
    //     pageType = EventConfirmationPageCardType.PENDING;
    //     break;
    //   default:
    //     pageType = EventConfirmationPageCardType.YES;
    //     break;
    // }

    // if (this.event) {
    //   this.eventService
    //     .getConfirmationPageByType(this.event.id, pageType)
    //     .subscribe({
    //       next: (res) => {
    //         this.eventConfirmationPage = res;
    //       },
    //     });
    // }
  }

  toTheEvent() {
    if (!this.event) return;
    this.router.navigate(['/', AppRoutes.Events.events, this.event.uri]);
    this.close();
  }
  canCardBeDisplayed(cardType: EventConfirmationPageCardType) {
    const card = this.findCardByType(cardType);
    switch (cardType) {
      case EventConfirmationPageCardType.SOCIAL_MEDIA: {
        if (
          !this.event ||
          !this.event.eventTemplate?.socialMedia ||
          this.event.eventTemplate.socialMedia.links.length === 0 ||
          this.event?.eventTemplate?.socialMedia?.links?.every(
            (link) => !link.link?.trim(),
          )
        ) {
          return false;
        }
        break;
      }
    }
    if (card) {
      return !card.hidden;
    }
    return true;
  }
  getBoughtTickets() {
    if (this.loggedUser && this.event) {
      this.eventService
        .getTicketOrders(this.event.id, true)
        .subscribe((ticketOrders) => {
          if (ticketOrders && ticketOrders.length > 0) {
            this.myOrders = ticketOrders;
          }
        });
    }
  }
  seeTickets(mode: string) {
    let ticket: EventAttendeeTicket | undefined;
    let order: EventAttendeeTicketOrder | undefined = this.ticketOrder;

    if (mode === 'user') {
      ticket = this.ticketOrder?.eventAttendeeTickets.find(
        (x) => x.eventAttendee?.userId === this.loggedUser?.id,
      );
    } else if (mode === 'allOrders') {
      order = undefined;
    }

    this.openGetBoughtTicketsDialog(ticket, order);
  }

  findCardByType(type: EventConfirmationPageCardType) {
    if (!this.event) return;
    return this.event.confirmationPageCards?.find((x) => x.type === type);
  }

  openGetBoughtTicketsDialog(
    ticket?: EventAttendeeTicket,
    order?: EventAttendeeTicketOrder,
  ) {
    const dialogRef = this.dialog.open(GetBoughtTicketsDialog, {
      maxWidth: '456px',
      maxHeight: '100vh',
      width: '100%',
      height: 'auto',
      data: {
        event: this.event,
        orders: this.myOrders,
        loggedUser: this.loggedUser,
        ticket: ticket,
        order: order,
      },
      panelClass: ['normal-dialog', 'new-normal-dialog'],
      backdropClass: '',
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res && res.refresh) {
        this.getBoughtTickets();
      }
    });
  }

  addToCalendar() {
    this.dialog.open(AddToCalendarDialog, {
      maxWidth: '602px',
      maxHeight: '100vh',
      width: '100%',
      height: 'auto',
      data: this.event,
      panelClass: ['normal-dialog', 'event-dialog', 'overlay-theme'],
    });
  }

  close() {
    this.dialogRef.close();
  }

  seeRsvpQRCode() {
    this.dialog.open(RsvpQrCodeDialog, {
      viewContainerRef: this.viewContainerRef,
      maxWidth: '602px',
      maxHeight: '100vh',
      width: '100%',
      height: 'auto',
      data: {
        event: this.event,
        attendee: this.attendee,
      },
      panelClass: ['normal-dialog', 'secondary-dialog'],
      backdropClass: '',
    });
  }

  get attendee(): EventAttendee | undefined {
    return this.event?.getAttendee(this.loggedUser?.id) || this.eventAttendee;
  }

  get isSportpageEvent(): boolean {
    return this.event?.type === EventType.SPORTPAGE;
  }

  get font(): EventTemplateFont | undefined {
    return this.event?.eventTemplate?.font;
  }

  get visual(): EventTemplateVisual | undefined {
    return this.event?.eventTemplate?.visual;
  }

  get afterScreenRsvpType(): RSVPOptionType {
    if (this.rsvpType === RSVPOptionType.NO) {
      return this.rsvpType;
    }

    if (this.onPendingList) {
      return RSVPOptionType.PENDING_APPROVAL;
    } else if (this.onWaitlist) {
      return RSVPOptionType.WAITLIST;
    }

    return this.rsvpType;
  }

  get isMizunoEvent(): boolean {
    return !!this.event?.uri.includes('mizuno');
  }

  get isUaEvent(): boolean {
    return !!this.event?.uri.includes('underarmour-infinite-elite-testival');
  }

  protected readonly RSVPOptionType = RSVPOptionType;
  protected readonly EventRSVPConfirmationPageSectionType =
    EventRSVPConfirmationPageSectionType;
  protected readonly EventWidget = EventWidget;
}
